import { useNavigate, useParams } from "@tanstack/solid-router";
import { createQuery } from "@tanstack/solid-query";
import { For, Show } from "solid-js";
import { CompanyPeopleRow } from "~/components/CompanyPeopleRow";
import { LookalikeChips } from "~/components/LookalikeChips";
import { ObservedRoleCard } from "~/components/ObservedRoleCard";
import { OrgMark } from "~/components/OrgMark";
import { PostedOpportunityCard } from "~/components/PostedOpportunityCard";
import { PresenceStats } from "~/components/PresenceStats";
import { StackRows } from "~/components/StackRows";
import { StatusPill } from "~/components/StatusPill";
import { fetchCompany } from "~/lib/api";
import { stackEntries } from "~/lib/company-stack";

export function CompanyDetailPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const slug = () => params().slug ?? "";

  const detailQuery = createQuery(() => ({
    queryKey: ["company", slug()],
    queryFn: () => fetchCompany(slug()),
    enabled: !!slug(),
  }));

  const item = () => detailQuery.data?.data;

  return (
    <section class="page page--flush company-detail">
      <Show when={detailQuery.isPending}>
        <p class="muted" style={{ padding: "20px" }}>
          Loading company…
        </p>
      </Show>
      <Show when={detailQuery.isError}>
        <div class="error-box" style={{ margin: "20px" }}>
          {(detailQuery.error as Error).message}
        </div>
        <button type="button" class="company-back" onClick={() => navigate({ to: "/companies" })}>
          Back to companies
        </button>
      </Show>
      <Show when={item()} keyed>
        {(co) => {
          const rows = stackEntries(co.stack);
          const communityOnly = co.confidence === "low";
          return (
          <>
            <button type="button" class="company-back" onClick={() => navigate({ to: "/companies" })}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M13 4L7 10l6 6"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Back to companies
            </button>

            <header class="company-detail-header">
              <OrgMark name={co.canonical_name} size="lg" />
              <p class="status-chip">
                {[co.sector, communityOnly ? "Community only" : co.confidence].filter(Boolean).join(" · ") ||
                  "Company"}
              </p>
              <h1>{co.canonical_name}</h1>
              <div class="company-detail-pills">
                <Show when={co.hiring}>
                  <StatusPill variant="hiring" />
                </Show>
                <Show when={co.locations}>
                  <span class="lookalike-chip">{co.locations}</span>
                </Show>
              </div>
            </header>

            <PresenceStats
              conference={co.conference_people}
              meetup={co.meetup_people}
              onConnect={co.members?.length}
            />

            <Show when={co.description || co.website || co.employee_band}>
              <div class="intel-copy-card">
                <p class="section-title section-title--inset">Who they are</p>
                <Show when={co.deb_relationship}>
                  <p class="muted">{co.deb_relationship}</p>
                </Show>
                <Show when={co.description}>
                  <p>{co.description}</p>
                </Show>
                <Show when={co.employee_band || co.employee_count_est}>
                  <p class="muted">
                    {[
                      co.employee_band,
                      co.employee_count_est ? `~${co.employee_count_est} people` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </Show>
                <Show when={co.website}>
                  <p>
                    <a href={co.website} target="_blank" rel="noreferrer">
                      {co.website}
                    </a>
                  </p>
                </Show>
                <Show when={co.last_verified}>
                  <p class="muted">Last verified {co.last_verified}</p>
                </Show>
              </div>
            </Show>

            <div class="company-detail-block">
              <h2 class="section-title section-title--inset">How they build</h2>
              <Show
                when={!communityOnly && rows.length}
                fallback={
                  <p class="muted">
                    {communityOnly
                      ? "Not shown. Confidence is low — we do not treat guesses as facts."
                      : "No sourced stack fields yet."}
                  </p>
                }
              >
                <StackRows rows={rows} />
              </Show>
            </div>

            <div class="intel-copy-card">
              <p class="section-title section-title--inset">People on Connect</p>
              <Show
                when={co.members?.length}
                fallback={
                  <p class="muted">
                    Nobody from {co.canonical_name} has opted in yet.
                    <Show when={co.conference_people}>
                      <span> {co.conference_people} people from this company have been to conference.</span>
                    </Show>
                  </p>
                }
              >
                <ul class="company-people-list">
                  <For each={co.members}>
                    {(card) => (
                      <li>
                        <CompanyPeopleRow
                          member={card}
                          onOpen={(id) => navigate({ to: "/profile/$profileId", params: { profileId: id } })}
                        />
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>

            <div class="company-detail-block">
              <h2 class="section-title section-title--inset">Observed roles</h2>
              <p class="muted">From public careers pages — not posted by a Connect member.</p>
              <Show
                when={co.roles?.length}
                fallback={<p class="muted">No observed openings right now.</p>}
              >
                <ul class="company-block-list">
                  <For each={co.roles}>
                    {(role) => (
                      <li>
                        <ObservedRoleCard role={role} />
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>

            <Show when={co.opportunities?.length}>
              <div class="company-detail-block">
                <h2 class="section-title section-title--inset">Posted on Connect</h2>
                <ul class="company-block-list">
                  <For each={co.opportunities}>
                    {(opp) => (
                      <li>
                        <PostedOpportunityCard
                          opportunity={opp}
                          onOpen={(id) =>
                            navigate({ to: "/opportunities/$opportunityId", params: { opportunityId: id } })
                          }
                        />
                      </li>
                    )}
                  </For>
                </ul>
              </div>
            </Show>

            <Show when={co.lookalikes?.length}>
              <div class="company-detail-block">
                <h2 class="section-title section-title--inset">People also look at</h2>
                <LookalikeChips
                  peers={co.lookalikes!}
                  onOpen={(peerSlug) => navigate({ to: "/companies/$slug", params: { slug: peerSlug } })}
                />
              </div>
            </Show>
          </>
          );
        }}
      </Show>
    </section>
  );
}
